import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
} from '@mui/material';
import { FC, memo } from 'react';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { ShipmentOrderLaneDetail } from 'src/entities/ShipmentOrderLane';
import { ShipmentOrderWaypointTimeEntity } from 'src/entities/ShipmentOrderResponse.entity';

const formatTimeRange = (time: ShipmentOrderWaypointTimeEntity) => {
  return [datetimeDecorator.toYyyyMmDdHhMm(new Date(time.startAt)),
    datetimeDecorator.toYyyyMmDdHhMm(new Date(time.endAt))].join(' 〜 ');
};

type Props = {
  detail: ShipmentOrderLaneDetail;
  isEditing?: boolean;
};

/**
 * レーン詳細 - 基本情報タブ
 */
const LaneDetailBasicInfo: FC<Props> = memo(({ detail, isEditing = false }) => (
  <Card>
    <CardContent>
      <Typography variant="h6" fontWeight="bold" mb={3}>基本情報</Typography>

      {/* 1️⃣ システム情報 */}
      <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
          システム情報
        </Typography>
        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">レーンID</Typography>
            <Typography variant="body1" fontWeight="medium">{detail.id}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">LEG数</Typography>
            <Typography variant="body1" fontWeight="medium">{detail.shipmentOrders?.length}件</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">伝票数</Typography>
            <Typography variant="body1" fontWeight="medium">{detail.shipmentOrders?.length}件</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 4, p: 3, bgcolor: 'blue.50', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
          荷主情報
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          {Array.from(new Set(detail.shipmentOrders)).map((shipmentOrder, index) => (
            <Box key={index} sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body1" fontWeight="bold" mb={1}>{shipmentOrder.shipperName}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, fontSize: '0.875rem' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">荷主担当者</Typography>
                  <Typography variant="body2">{shipmentOrder.shipperContactPerson}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">連絡先</Typography>
                  <Typography variant="body2">
                    TEL: {shipmentOrder.shipperPhoneNumber}<br />
                    MAIL: {shipmentOrder.shipperEmailAddress}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ mb: 4, p: 3, bgcolor: 'green.50', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
          積地情報
        </Typography>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">積地名称</Typography>
            <Typography variant="body1" fontWeight="medium">{detail.shipmentOrders[0].shipmentOrderLoadingWaypoint.name}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">積地住所</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={detail.shipmentOrders[0].shipmentOrderLoadingWaypoint.address} // {editData.pickupAddress}
                // onChange={(e) => handleInputChange('pickupAddress', e.target.value)}
              />
            ) : (
              <Typography variant="body1" fontWeight="medium">{detail.shipmentOrders[0].shipmentOrderLoadingWaypoint.address}</Typography>
            )}
          </Box>
          <Box sx={{ gridColumn: '1 / 3' }}>
            <Typography variant="subtitle2" color="text.secondary">時間枠</Typography>
            {detail.shipmentOrders[0].shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.map((time, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="body1" fontWeight="medium">
                  {formatTimeRange(time)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ gridColumn: '1 / 3' }}>
            <Typography variant="subtitle2" color="text.secondary">滞在時間</Typography>
            <Typography variant="body1" fontWeight="medium">
              {detail.shipmentOrders[0].shipmentOrderLoadingWaypoint.stayingMinutes ?
                `${detail.shipmentOrders[0].shipmentOrderLoadingWaypoint.stayingMinutes}分` : '-'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 4, p: 3, bgcolor: 'green.50', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
          降地情報
        </Typography>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">降地名称</Typography>
            <Typography variant="body1" fontWeight="medium">{detail.shipmentOrders[0].shipmentOrderUnloadingWaypoint.name}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">降地住所</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={detail.shipmentOrders[0].shipmentOrderUnloadingWaypoint.address} // {editData.pickupAddress}
                // onChange={(e) => handleInputChange('pickupAddress', e.target.value)}
              />
            ) : (
              <Typography variant="body1" fontWeight="medium">{detail.shipmentOrders[0].shipmentOrderUnloadingWaypoint.address}</Typography>
            )}
          </Box>
          <Box sx={{ gridColumn: '1 / 3' }}>
            <Typography variant="subtitle2" color="text.secondary">時間枠</Typography>
            {detail.shipmentOrders[0].shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.map((time, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="body1" fontWeight="medium">
                  {formatTimeRange(time)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ gridColumn: '1 / 3' }}>
            <Typography variant="subtitle2" color="text.secondary">滞在時間</Typography>
            <Typography variant="body1" fontWeight="medium">
              {detail.shipmentOrders[0].shipmentOrderUnloadingWaypoint.stayingMinutes ?
                `${detail.shipmentOrders[0].shipmentOrderUnloadingWaypoint.stayingMinutes}分` : '-'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 4, p: 3, bgcolor: 'red.50', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
          制約情報
        </Typography>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>指定車両タイプ</Typography>
            <Typography variant="body2" color="text.secondary">
              {Array.from(new Set(detail.shipmentOrders.flatMap((so) => so.designatedTruckKlasses))).join(', ') || ' - '}
            </Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>指定車種</Typography>
            <Typography variant="body2" color="text.secondary">
              {Array.from(new Set(detail.shipmentOrders.flatMap((so) => so.designatedTruckCarModels))).join(', ') || ' - '}
            </Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>荷台高さ</Typography>
            <Typography variant="body2" color="text.secondary">
              {Array.from(new Set(detail.shipmentOrders.flatMap((so) => so.designatedTruckLoadingPlatformHeights))).join(', ') || ' - '}
            </Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>荷台幅</Typography>
            <Typography variant="body2" color="text.secondary">
              {Array.from(new Set(detail.shipmentOrders.flatMap((so) => so.designatedTruckLoadingPlatformWidths))).join(', ') || ' - '}
            </Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>荷台長さ</Typography>
            <Typography variant="body2" color="text.secondary">
              {Array.from(new Set(detail.shipmentOrders.flatMap((so) => so.designatedTruckLoadingPlatformLengths))).join(', ') || ' - '}
            </Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>床仕様</Typography>
            <Typography variant="body2" color="text.secondary">
              {Array.from(new Set(detail.shipmentOrders.flatMap((so) => so.designatedTruckFloorSpecifications))).join(', ') || ' - '}
            </Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>装置</Typography>
            <Typography variant="body2" color="text.secondary">
              {Array.from(new Set(detail.shipmentOrders.flatMap((so) => so.designatedTruckFeatures))).join(', ') || ' - '}
            </Typography>
          </Box>
        </Box>
      </Box>

    </CardContent>
  </Card>
));

export default LaneDetailBasicInfo;
